import { countriesByCode } from "@/shared/utils/countries";
import type { PlayerMetricsPoint } from "./types";

// ISO 3166-1 alpha-3 в alpha-2: Cloudflare отдаёт страну CMCD тремя
// буквами, а Intl.DisplayNames и флаги работают с двумя.
const ISO3_TO_ISO2_PAIRS =
  "AFG:AF ALA:AX ALB:AL DZA:DZ ASM:AS AND:AD AGO:AO AIA:AI ATA:AQ ATG:AG " +
  "ARG:AR ARM:AM ABW:AW AUS:AU AUT:AT AZE:AZ BHS:BS BHR:BH BGD:BD BRB:BB " +
  "BLR:BY BEL:BE BLZ:BZ BEN:BJ BMU:BM BTN:BT BOL:BO BES:BQ BIH:BA BWA:BW " +
  "BVT:BV BRA:BR IOT:IO BRN:BN BGR:BG BFA:BF BDI:BI CPV:CV KHM:KH CMR:CM " +
  "CAN:CA CYM:KY CAF:CF TCD:TD CHL:CL CHN:CN CXR:CX CCK:CC COL:CO COM:KM " +
  "COG:CG COD:CD COK:CK CRI:CR CIV:CI HRV:HR CUB:CU CUW:CW CYP:CY CZE:CZ " +
  "DNK:DK DJI:DJ DMA:DM DOM:DO ECU:EC EGY:EG SLV:SV GNQ:GQ ERI:ER EST:EE " +
  "SWZ:SZ ETH:ET FLK:FK FRO:FO FJI:FJ FIN:FI FRA:FR GUF:GF PYF:PF ATF:TF " +
  "GAB:GA GMB:GM GEO:GE DEU:DE GHA:GH GIB:GI GRC:GR GRL:GL GRD:GD GLP:GP " +
  "GUM:GU GTM:GT GGY:GG GIN:GN GNB:GW GUY:GY HTI:HT HMD:HM VAT:VA HND:HN " +
  "HKG:HK HUN:HU ISL:IS IND:IN IDN:ID IRN:IR IRQ:IQ IRL:IE IMN:IM ISR:IL " +
  "ITA:IT JAM:JM JPN:JP JEY:JE JOR:JO KAZ:KZ KEN:KE KIR:KI PRK:KP KOR:KR " +
  "KWT:KW KGZ:KG LAO:LA LVA:LV LBN:LB LSO:LS LBR:LR LBY:LY LIE:LI LTU:LT " +
  "LUX:LU MAC:MO MKD:MK MDG:MG MWI:MW MYS:MY MDV:MV MLI:ML MLT:MT MHL:MH " +
  "MTQ:MQ MRT:MR MUS:MU MYT:YT MEX:MX FSM:FM MDA:MD MCO:MC MNG:MN MNE:ME " +
  "MSR:MS MAR:MA MOZ:MZ MMR:MM NAM:NA NRU:NR NPL:NP NLD:NL NCL:NC NZL:NZ " +
  "NIC:NI NER:NE NGA:NG NIU:NU NFK:NF MNP:MP NOR:NO OMN:OM PAK:PK PLW:PW " +
  "PSE:PS PAN:PA PNG:PG PRY:PY PER:PE PHL:PH PCN:PN POL:PL PRT:PT PRI:PR " +
  "QAT:QA REU:RE ROU:RO RUS:RU RWA:RW BLM:BL SHN:SH KNA:KN LCA:LC MAF:MF " +
  "SPM:PM VCT:VC WSM:WS SMR:SM STP:ST SAU:SA SEN:SN SRB:RS SYC:SC SLE:SL " +
  "SGP:SG SXM:SX SVK:SK SVN:SI SLB:SB SOM:SO ZAF:ZA SGS:GS SSD:SS ESP:ES " +
  "LKA:LK SDN:SD SUR:SR SJM:SJ SWE:SE CHE:CH SYR:SY TWN:TW TJK:TJ TZA:TZ " +
  "THA:TH TLS:TL TGO:TG TKL:TK TON:TO TTO:TT TUN:TN TUR:TR TKM:TM TCA:TC " +
  "TUV:TV UGA:UG UKR:UA ARE:AE GBR:GB USA:US UMI:UM URY:UY UZB:UZ VUT:VU " +
  "VEN:VE VNM:VN VGB:VG VIR:VI WLF:WF ESH:EH YEM:YE ZMB:ZM ZWE:ZW XKX:XK";

export const ISO3_TO_ISO2: Record<string, string> = Object.fromEntries(
  ISO3_TO_ISO2_PAIRS.split(" ").map((pair) => pair.split(":"))
);

/** Двухбуквенный код страны из ключа Cloudflare (две или три буквы). */
export function toIso2(key: string): string | null {
  const code = key.trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(code)) return code;
  if (/^[A-Z]{3}$/.test(code)) return ISO3_TO_ISO2[code] ?? null;
  return null;
}

/** Флаг из regional indicator symbols: «DE» → 🇩🇪. */
export function countryFlag(iso2: string): string {
  return String.fromCodePoint(
    ...[...iso2.toUpperCase()].map((char) => 0x1f1e6 + char.charCodeAt(0) - 65)
  );
}

/** «🇩🇪 Германия»: локализованное название с флагом, иначе ключ как есть. */
export function formatCountry(key: string, lng: string): string {
  const iso2 = toIso2(key);
  if (!iso2) return key;
  let name: string | undefined;
  try {
    name = new Intl.DisplayNames([lng], { type: "region" }).of(iso2);
  } catch {
    name = undefined;
  }
  const label = name && name !== iso2 ? name : (countriesByCode[iso2] ?? key);
  return `${countryFlag(iso2)} ${label}`;
}

/**
 * Разрешение как «1080p»: из «1920x1080» берём меньшую сторону (у
 * вертикального видео с телефона «1080x1920» это тоже 1080p), голое число
 * дополняем «p».
 */
export function formatResolution(key: string): string {
  const value = key.trim();
  const size = /^(\d+)\s*[xX×]\s*(\d+)$/.exec(value);
  if (size) {
    return `${Math.min(Number(size[1]), Number(size[2]))}p`;
  }
  if (/^\d+$/.test(value)) return `${value}p`;
  return value;
}

/**
 * Есть ли у ряда данные о подвисаниях. Плеер витрины (hls.js, CMCD v1)
 * передаёт только флаг `bs`, длительности ожидания в CMCD v1 нет, поэтому
 * Cloudflare отдаёт пусто или ноль. Ровную линию нуля не рисуем: это не
 * «подвисаний не было», а «плееры не сообщают».
 */
export function hasStallData(points: PlayerMetricsPoint[]): boolean {
  return points.some(
    (point) =>
      typeof point.bufferStarvationSeconds === "number" &&
      point.bufferStarvationSeconds > 0
  );
}
